import { parseWeight } from './weightUtils';

/**
 * Aggregate all ingredients across recipe parts into a sorted summary.
 * @param {Array} parts - Array of recipe parts, each containing steps with ingredients
 * @returns {Array} - Sorted array of [name, { number, unit, mixedUnits }] entries
 */
export const aggregateIngredients = (parts) => {
    const totalIngredients = {};

    parts.forEach(part => {
        part.steps.forEach(step => {
            step.ingredients.forEach(ingredient => {
                if (ingredient.name && ingredient.weight) {
                    const name = ingredient.name.trim();
                    const { number, unit, raw } = parseWeight(ingredient.weight);

                    if (totalIngredients[name]) {
                        if (totalIngredients[name].unit === unit) {
                            totalIngredients[name].number += number;
                        } else {
                            totalIngredients[name].number += number;
                            totalIngredients[name].mixedUnits = true;
                        }
                    } else {
                        totalIngredients[name] = { number, unit, mixedUnits: false, raw };
                    }
                }
            });
        });
    });

    return Object.entries(totalIngredients)
        .filter(([name]) => !name.endsWith('*'))
        .sort(([a], [b]) => a.localeCompare(b));
};

/**
 * Scale nutrition facts from a base serving count to a desired serving count.
 * @param {Object} baseFacts - Original nutrition facts object
 * @param {number} baseServings - Original number of servings
 * @param {number} desiredServings - Desired number of servings
 * @returns {Object} - Scaled nutrition facts
 */
export const scaleNutritionFacts = (baseFacts, baseServings, desiredServings) => {
    const ratio = desiredServings / baseServings;

    return {
        calories: Math.round(baseFacts.calories * ratio),
        totalFat: Math.round(baseFacts.totalFat * ratio * 10) / 10,
        saturatedFat: Math.round(baseFacts.saturatedFat * ratio * 10) / 10,
        transFat: Math.round(baseFacts.transFat * ratio * 10) / 10,
        polyunsaturatedFat: Math.round(baseFacts.polyunsaturatedFat * ratio * 10) / 10,
        monounsaturatedFat: Math.round(baseFacts.monounsaturatedFat * ratio * 10) / 10,
        cholesterol: Math.round(baseFacts.cholesterol * ratio),
        sodium: Math.round(baseFacts.sodium * ratio),
        totalCarbohydrates: Math.round(baseFacts.totalCarbohydrates * ratio * 10) / 10,
        dietaryFiber: Math.round(baseFacts.dietaryFiber * ratio * 10) / 10,
        sugar: Math.round(baseFacts.sugar * ratio * 10) / 10,
        addedSugar: Math.round(baseFacts.addedSugar * ratio * 10) / 10,
        sugarAlcohols: Math.round(baseFacts.sugarAlcohols * ratio * 10) / 10,
        netCarbs: Math.round(baseFacts.netCarbs * ratio * 10) / 10,
        protein: Math.round(baseFacts.protein * ratio * 10) / 10,
    };
};

/**
 * Empty nutrition facts object (all zeros).
 */
export const EMPTY_NUTRITION = {
    calories: 0,
    totalFat: 0,
    saturatedFat: 0,
    transFat: 0,
    polyunsaturatedFat: 0,
    monounsaturatedFat: 0,
    cholesterol: 0,
    sodium: 0,
    totalCarbohydrates: 0,
    dietaryFiber: 0,
    sugar: 0,
    addedSugar: 0,
    sugarAlcohols: 0,
    netCarbs: 0,
    protein: 0,
};
